import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import { getSharedReport } from '../services/reportService';
import ReportAnalysisContent from '../components/dashboard/ReportAnalysisContent';
import AISummaryCard from '../components/dashboard/AISummaryCard';
import { ReportDetailSkeleton } from '../components/dashboard/PageSkeletons';
import { getReportBrandName } from '../utils/reportAnalysisUtils';

/**
 * Public read-only report view — no authentication required
 */
const SharedReport = () => {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [shareMeta, setShareMeta] = useState(null);
  const [metricIndex, setMetricIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getSharedReport(token);
        setReport(data.report);
        setShareMeta(data.share);
        setMetricIndex(0);
      } catch (err) {
        const message =
          err.response?.status === 410
            ? 'Bu paylaşım linkinin süresi dolmuş.'
            : 'Paylaşılan rapor yüklenemedi veya link geçersiz.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchShared();
  }, [token]);

  const handleMetricChange = useCallback((index) => {
    setMetricIndex(index);
  }, []);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', py: 4, px: 2 }}>
        <ReportDetailSkeleton />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', mt: 6, px: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button component={RouterLink} to="/login" variant="contained" sx={{ bgcolor: '#240B4B' }}>
          Giriş Yap
        </Button>
      </Box>
    );
  }

  const brandName = getReportBrandName(report);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 4, px: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          HypeCRO — Paylaşılan Rapor
        </Typography>
        {shareMeta?.expiresAt && (
          <Chip
            size="small"
            label={`Geçerlilik: ${new Date(shareMeta.expiresAt).toLocaleDateString('tr-TR')}`}
            variant="outlined"
          />
        )}
      </Box>

      <Paper
        id="ga4-abtest-results"
        className="report-detail-page"
        elevation={0}
        sx={{ borderRadius: 2.5, boxShadow: '0 0 15px rgba(0,0,0,0.08)', p: 2.5 }}
      >
        <Box id="ga4-abtest-content">
          <Box sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid #eee' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#240B4B', mb: 0.5 }}>
              {report.reportName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {brandName}
              </Typography>
              {report.status && (
                <Chip label={report.status} size="small" color="default" />
              )}
            </Box>
          </Box>

          <AISummaryCard aiSummary={report.aiSummary} readOnly />

          <ReportAnalysisContent
            originalData={report}
            metricIndex={metricIndex}
            onMetricChange={handleMetricChange}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default SharedReport;
